import moment from 'moment-timezone';
import { cleanPortalDid, isValidUSNumber, toE164Default10DigitUs } from '@nuso/common/util-did';
import { emailRegex } from '@nuso/forms/util';
import type { TnCreatePortOrderAddressInput } from './tn-porting';
import { validatePortOrderAddress, validateSubscriberNameFields } from './tn-porting-validation';

// POR-206 split file: one row per TN, one column per editable port-info field. Rows
// whose values are equal (after normalization) become one order. An SOA-only order's
// file carries only the columns an SOA order uses (see SOA_ONLY_SPLIT_COLUMNS).

export type TnPortOrderSplitAddressValues = Required<TnCreatePortOrderAddressInput>;

// The 34 non-TN values of a row. Due date and time are wall-clock strings in the
// request's time zone; LOA date is a calendar day and is never converted.
export interface TnPortOrderSplitRowValues {
  dueDate: string; // YYYY-MM-DD or ''
  dueTime: string; // HH:mm or ''
  isEarliestPossible: boolean;
  loaDate: string; // YYYY-MM-DD or ''
  autoActivation: boolean;
  isFullPort: boolean;
  isResidential: boolean;
  businessName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  authorizationName: string;
  billingTelephoneNumber: string;
  newBillingTelephoneNumber: string;
  accountNumber: string;
  accountPin: string;
  notificationEmails: string[];
  address: TnPortOrderSplitAddressValues;
}

// row is the 1-based data row (the spreadsheet line minus the header); null for
// errors about the whole file.
export interface TnPortOrderSplitRowError {
  row: number | null;
  column: string | null;
  message: string;
}

type ScalarKey = Exclude<keyof TnPortOrderSplitRowValues, 'address' | 'notificationEmails'>;
type BooleanKey = { [K in ScalarKey]: TnPortOrderSplitRowValues[K] extends boolean ? K : never }[ScalarKey];
type StringKey = Exclude<ScalarKey, BooleanKey>;
type AddressKey = keyof TnPortOrderSplitAddressValues;

type SplitColumn =
  | { header: string; kind: 'string'; key: StringKey }
  | { header: string; kind: 'boolean'; key: BooleanKey }
  | { header: string; kind: 'emails' }
  | { header: string; kind: 'address'; key: AddressKey };

export const TN_PORT_ORDER_SPLIT_CSV_TN_HEADER = 'TN';
export const TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER = 'Due Date (Local)';
export const TN_PORT_ORDER_SPLIT_CSV_DUE_TIME_HEADER = 'Due Time (Local)';

// Every column after TN, in file order.
const SPLIT_COLUMNS: readonly SplitColumn[] = [
  { header: TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER, kind: 'string', key: 'dueDate' },
  { header: TN_PORT_ORDER_SPLIT_CSV_DUE_TIME_HEADER, kind: 'string', key: 'dueTime' },
  { header: 'Earliest Possible', kind: 'boolean', key: 'isEarliestPossible' },
  { header: 'LOA Date', kind: 'string', key: 'loaDate' },
  { header: 'Auto Activation', kind: 'boolean', key: 'autoActivation' },
  { header: 'Full Port', kind: 'boolean', key: 'isFullPort' },
  { header: 'Residential', kind: 'boolean', key: 'isResidential' },
  { header: 'Business Name', kind: 'string', key: 'businessName' },
  { header: 'First Name', kind: 'string', key: 'firstName' },
  { header: 'Middle Name', kind: 'string', key: 'middleName' },
  { header: 'Last Name', kind: 'string', key: 'lastName' },
  { header: 'Authorization Name', kind: 'string', key: 'authorizationName' },
  { header: 'BTN', kind: 'string', key: 'billingTelephoneNumber' },
  { header: 'New BTN', kind: 'string', key: 'newBillingTelephoneNumber' },
  { header: 'Account Number', kind: 'string', key: 'accountNumber' },
  { header: 'Account PIN', kind: 'string', key: 'accountPin' },
  { header: 'Notification Emails', kind: 'emails' },
  { header: 'Number Prefix', kind: 'address', key: 'numberPrefix' },
  { header: 'Number', kind: 'address', key: 'number' },
  { header: 'Number Suffix', kind: 'address', key: 'numberSuffix' },
  { header: 'Street Pre Directional', kind: 'address', key: 'streetPreDirectional' },
  { header: 'Street Name', kind: 'address', key: 'streetName' },
  { header: 'Street Suffix', kind: 'address', key: 'streetSuffix' },
  { header: 'Street Post Directional', kind: 'address', key: 'streetPostDirectional' },
  { header: 'Location Designator 1', kind: 'address', key: 'locationDesignator1' },
  { header: 'Location Value 1', kind: 'address', key: 'locationValue1' },
  { header: 'Location Designator 2', kind: 'address', key: 'locationDesignator2' },
  { header: 'Location Value 2', kind: 'address', key: 'locationValue2' },
  { header: 'Location Designator 3', kind: 'address', key: 'locationDesignator3' },
  { header: 'Location Value 3', kind: 'address', key: 'locationValue3' },
  { header: 'City', kind: 'address', key: 'city' },
  { header: 'State', kind: 'address', key: 'stateProvince' },
  { header: 'Zip', kind: 'address', key: 'zipCode' },
  { header: 'Country', kind: 'address', key: 'country' },
];

const NOTIFICATION_EMAILS_HEADER = 'Notification Emails';

// An SOA-only order sends the carrier no LSR, so it has no use for the LOA, subscriber,
// billing or service-address columns. Its file leaves them out and every resulting
// order keeps the original's values for them.
const SOA_ONLY_SPLIT_HEADERS = new Set([
  TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER,
  TN_PORT_ORDER_SPLIT_CSV_DUE_TIME_HEADER,
  'Earliest Possible',
  'Auto Activation',
  NOTIFICATION_EMAILS_HEADER,
]);
const SOA_ONLY_SPLIT_COLUMNS = SPLIT_COLUMNS.filter(c => SOA_ONLY_SPLIT_HEADERS.has(c.header));

const splitColumns = (soaOnly: boolean): readonly SplitColumn[] => (soaOnly ? SOA_ONLY_SPLIT_COLUMNS : SPLIT_COLUMNS);

// The file's headers, TN first, in file order.
export function tnPortOrderSplitCsvHeaders(soaOnly = false): readonly string[] {
  return [TN_PORT_ORDER_SPLIT_CSV_TN_HEADER, ...splitColumns(soaOnly).map(c => c.header)];
}

export const TN_PORT_ORDER_SPLIT_CSV_HEADERS: readonly string[] = tnPortOrderSplitCsvHeaders();
const DATE_FORMAT = 'YYYY-MM-DD';
const TIME_FORMAT = 'HH:mm';
// Spreadsheet apps rewrite ISO dates and 24-hour times on save, so the parser also
// takes their forms and normalizes to the canonical ones. Strict parsing takes a
// leading zero only from a two-letter token, hence both widths.
const DATE_INPUT_FORMATS = [DATE_FORMAT, 'M/D/YYYY', 'MM/DD/YYYY', 'M/D/YY', 'MM/DD/YY'];
const TIME_INPUT_FORMATS = ['H:mm', 'HH:mm', 'H:mm:ss', 'HH:mm:ss', 'h:mm A', 'hh:mm A', 'h:mm:ss A', 'hh:mm:ss A'];
const ANCHORED_EMAIL_REGEX = new RegExp(`^(?:${emailRegex.source})$`);

const formatBoolean = (v: boolean): string => (v ? 'Y' : 'N');

const normalizeEmails = (emails: string[]): string[] =>
  [...new Set(emails.map(e => e.trim().toLowerCase()).filter(e => e))].sort();

// The column's value as written to the file and as compared for grouping.
function columnString(values: TnPortOrderSplitRowValues, column: SplitColumn): string {
  switch (column.kind) {
    case 'string':
      return values[column.key].trim();
    case 'boolean':
      return formatBoolean(values[column.key]);
    case 'emails':
      return normalizeEmails(values.notificationEmails).join(';');
    case 'address':
      return values.address[column.key].trim();
  }
}

// The row as CSV cells, TN first, in tnPortOrderSplitCsvHeaders order.
export function splitRowCells(tn: string, values: TnPortOrderSplitRowValues, soaOnly = false): string[] {
  return [tn, ...splitColumns(soaOnly).map(c => columnString(values, c))];
}

// Equal keys mean the same group.
export function splitRowKey(values: TnPortOrderSplitRowValues, soaOnly = false): string {
  return JSON.stringify(splitColumns(soaOnly).map(c => columnString(values, c)));
}

// The headers of the columns whose values differ, in file order.
export function diffSplitRowValues(a: TnPortOrderSplitRowValues, b: TnPortOrderSplitRowValues, soaOnly = false): string[] {
  return splitColumns(soaOnly).filter(c => columnString(a, c) !== columnString(b, c)).map(c => c.header);
}

function normalizeDate(raw: string): string | null {
  const m = moment(raw, DATE_INPUT_FORMATS, true);
  return m.isValid() ? m.format(DATE_FORMAT) : null;
}

function normalizeTime(raw: string): string | null {
  const m = moment(raw.toUpperCase(), TIME_INPUT_FORMATS, true);
  return m.isValid() ? m.format(TIME_FORMAT) : null;
}

function parseBoolean(raw: string): boolean | null {
  const v = raw.trim().toUpperCase();
  if (v === 'Y' || v === 'YES') return true;
  if (v === 'N' || v === 'NO') return false;
  return null;
}

// A 10-digit (or +1) US TN in E.164, or null.
export function parseSplitTn(raw: string): string | null {
  const digits = cleanPortalDid(raw.trim());
  return digits && isValidUSNumber(digits) ? toE164Default10DigitUs(digits) : null;
}

export interface TnPortOrderSplitParsedRow {
  row: number;
  tnE164: string | null;
  values: TnPortOrderSplitRowValues;
  errors: TnPortOrderSplitRowError[];
}

export interface ReadSplitRowOptions {
  soaOnly?: boolean;
  // The values of the columns the file leaves out (an SOA-only order's), for the
  // row's TN. Defaults to blank.
  baseValues?: (tnE164: string | null) => TnPortOrderSplitRowValues;
}

// Reads one data row keyed by header into values. Format errors (TN, Y/N, date and
// time) are reported here and the field is left blank/false; the field rules are in
// validateSplitRowValues. Dates and times are normalized to YYYY-MM-DD and HH:mm,
// and state and country are upper-cased.
export function readSplitRow(
  row: number,
  record: Record<string, string | undefined>,
  { soaOnly = false, baseValues = emptySplitRowValues }: ReadSplitRowOptions = {},
): TnPortOrderSplitParsedRow {
  const errors: TnPortOrderSplitRowError[] = [];
  const cell = (header: string): string => (record[header] ?? '').trim();
  const error = (column: string, message: string) => errors.push({ row, column, message });

  const rawTn = cell(TN_PORT_ORDER_SPLIT_CSV_TN_HEADER);
  const tnE164 = parseSplitTn(rawTn);
  if (!rawTn) {
    error(TN_PORT_ORDER_SPLIT_CSV_TN_HEADER, 'TN is required.');
  } else if (!tnE164) {
    error(TN_PORT_ORDER_SPLIT_CSV_TN_HEADER, `${rawTn} is not a valid 10-digit US number.`);
  }

  const base = baseValues(tnE164);
  const values = { ...base, address: { ...base.address } };
  for (const column of splitColumns(soaOnly)) {
    const raw = cell(column.header);
    switch (column.kind) {
      case 'string':
        values[column.key] = raw;
        break;
      case 'boolean': {
        const b = parseBoolean(raw);
        if (b === null) {
          error(column.header, `${column.header} must be Y or N.`);
        }
        values[column.key] = b ?? false;
        break;
      }
      case 'emails':
        values.notificationEmails = raw.split(/[;,]/).map(e => e.trim()).filter(e => e);
        break;
      case 'address':
        values.address[column.key] = column.key === 'stateProvince' || column.key === 'country' ? raw.toUpperCase() : raw;
        break;
    }
  }

  for (const [key, header, normalize, format] of [
    ['dueDate', TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER, normalizeDate, DATE_FORMAT],
    ['dueTime', TN_PORT_ORDER_SPLIT_CSV_DUE_TIME_HEADER, normalizeTime, TIME_FORMAT],
    ['loaDate', 'LOA Date', normalizeDate, DATE_FORMAT],
  ] as const) {
    if (values[key] && record[header] !== undefined) {
      const normalized = normalize(values[key]);
      if (normalized === null) {
        error(header, `${header} must be ${format}.`);
      }
      values[key] = normalized ?? '';
    }
  }

  return { row, tnE164, values, errors };
}

export function emptySplitRowValues(): TnPortOrderSplitRowValues {
  return {
    dueDate: '',
    dueTime: '',
    isEarliestPossible: false,
    loaDate: '',
    autoActivation: false,
    isFullPort: false,
    isResidential: false,
    businessName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    authorizationName: '',
    billingTelephoneNumber: '',
    newBillingTelephoneNumber: '',
    accountNumber: '',
    accountPin: '',
    notificationEmails: [],
    address: {
      numberPrefix: '',
      number: '',
      numberSuffix: '',
      streetPreDirectional: '',
      streetName: '',
      streetSuffix: '',
      streetPostDirectional: '',
      locationDesignator1: '',
      locationValue1: '',
      locationDesignator2: '',
      locationValue2: '',
      locationDesignator3: '',
      locationValue3: '',
      city: '',
      stateProvince: '',
      zipCode: '',
      country: '',
    },
  };
}

const isUsPhone = (v: string): boolean => isValidUSNumber(cleanPortalDid(v));

// The field rules of one row. Row and column are filled in for each error; the
// due-date business rules (window, increments, lead time, holidays) run on the
// resolved instant at apply time, not here. An SOA-only row is checked only on the
// columns its file has.
export function validateSplitRowValues(
  values: TnPortOrderSplitRowValues,
  row: number | null = null,
  soaOnly = false,
): TnPortOrderSplitRowError[] {
  const errors: TnPortOrderSplitRowError[] = [];
  const error = (column: string | null, message: string) => errors.push({ row, column, message });

  if (!values.dueDate && !values.isEarliestPossible) {
    error(TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER, 'Due Date (Local) is required unless Earliest Possible is Y.');
  }
  if (values.dueDate && !values.dueTime) {
    error(TN_PORT_ORDER_SPLIT_CSV_DUE_TIME_HEADER, 'Due Time (Local) is required with a due date.');
  }
  if (!values.dueDate && values.dueTime) {
    error(TN_PORT_ORDER_SPLIT_CSV_DUE_DATE_HEADER, 'Due Date (Local) is required with a due time.');
  }
  const badEmails = values.notificationEmails.filter(e => !ANCHORED_EMAIL_REGEX.test(e.trim().toLowerCase()));
  if (badEmails.length > 0) {
    error(NOTIFICATION_EMAILS_HEADER, `Not a valid email address: ${badEmails.join(', ')}.`);
  }
  if (soaOnly) {
    return errors;
  }

  const nameError = validateSubscriberNameFields(values);
  if (nameError) {
    error(null, nameError);
  }
  if (!values.authorizationName.trim()) {
    error('Authorization Name', 'Authorization Name is required.');
  }
  if (!values.billingTelephoneNumber.trim()) {
    error('BTN', 'BTN is required.');
  } else if (!isUsPhone(values.billingTelephoneNumber)) {
    error('BTN', 'BTN must be a 10-digit US number.');
  }
  if (values.newBillingTelephoneNumber.trim() && !isUsPhone(values.newBillingTelephoneNumber)) {
    error('New BTN', 'New BTN must be a 10-digit US number.');
  }
  if (!values.accountNumber.trim()) {
    error('Account Number', 'Account Number is required.');
  }

  const addressError = validatePortOrderAddress(values.address);
  if (addressError) {
    error(null, addressError);
  }
  return errors;
}

// The zone when moment knows it; otherwise throws, so an unknown zone fails the
// request instead of being read as UTC.
export function resolveSplitTimeZone(timeZone: string): string {
  if (!timeZone || !moment.tz.zone(timeZone)) {
    throw new Error(`Unknown time zone: ${timeZone}`);
  }
  return timeZone;
}
